import React, { useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { InputGroupInput, cn } from '@column/ui';

import { inertWhenHidden } from '~/lib/components/selectors/primitives/inert';

interface SectionResetPasswordProps {
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
}

const FIELD_CLASS_NAME =
  'h-10 px-4 pr-11 autofill:[-webkit-text-fill-color:currentColor] autofill:[transition:background-color_9999s_ease-in-out_0s]';

export const SectionResetPassword = ({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
}: SectionResetPasswordProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const showConfirm = newPassword.length > 0;
  const hasConfirmError = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const inputType = isVisible ? 'text' : 'password';

  const visibilityToggle = (
    <button
      type="button"
      aria-label={isVisible ? 'Hide passwords' : 'Show passwords'}
      aria-pressed={isVisible}
      onClick={() => setIsVisible((visible) => !visible)}
      className="absolute top-1/2 right-2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none dark:text-gray-500 dark:hover:text-gray-300"
    >
      <span key={inputType} className="animate-in duration-150 fade-in zoom-in-75">
        {isVisible ? (
          <Icon.EyeClosed className="[--icon-size-x:16px] [--icon-size-y:16px]" />
        ) : (
          <Icon.Eye className="[--icon-size-x:16px] [--icon-size-y:16px]" />
        )}
      </span>
    </button>
  );

  return (
    <div
      data-cy="password-fields"
      aria-invalid={hasConfirmError || undefined}
      className={cn(
        'w-full overflow-clip rounded-lg bg-[rgb(87_90_100/0.04)] inset-shadow-input shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,background-color] duration-200',
        'hover:bg-[rgb(87_90_100/0.06)] hover:[--input-border-color:rgb(43_49_67/0.125)]',
        'focus-within:bg-white focus-within:[--input-border-color:var(--color-blue-500)] focus-within:[--input-ring-color:rgb(69_117_205/0.15)] focus-within:[--input-ring-size:3px]',
        'dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:focus-within:bg-input/30 dark:focus-within:[--input-border-color:var(--color-blue-500)]',
        'aria-invalid:[--input-border-color:var(--color-destructive)] aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] aria-invalid:[--input-ring-size:3px]'
      )}
    >
      <div className="relative">
        <InputGroupInput
          type={inputType}
          autoComplete="new-password"
          placeholder="New Password"
          aria-label="New Password"
          value={newPassword}
          onChange={(event) => onNewPasswordChange(event.target.value)}
          className={FIELD_CLASS_NAME}
        />
        {visibilityToggle}
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-250 ease-out',
          showConfirm ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
        {...inertWhenHidden(!showConfirm)}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="relative border-t border-[rgb(43_49_67/0.075)] dark:border-white/10">
            <InputGroupInput
              type={inputType}
              autoComplete="new-password"
              placeholder="Confirm Password"
              aria-label="Confirm Password"
              aria-invalid={hasConfirmError || undefined}
              value={confirmPassword}
              onChange={(event) => onConfirmPasswordChange(event.target.value)}
              className={FIELD_CLASS_NAME}
            />
            {visibilityToggle}
          </div>
        </div>
      </div>
    </div>
  );
};
